<template>
  <v-card
    :to="routerTo"
    :ripple="false" tile flat
    class="battle-listing grey600 pa-2"
  >
    <div class="game-info align-self-center">
      <div class="px-2 mb-4 d-flex align-center">
        <div
          v-if="game.config.funding"
          :class="{'is-free': !game.joinPrice}"
          class="text-caption grey200--text non-funded-price"
        >
          {{ game.price | toScrap }}
        </div>
        <scrap v-if="game.joinPrice" class="text-body-1" :amount="game.joinPrice" />
        <span v-else class="green400--text font-weight-bold mr-1">Free</span>
        <v-tooltip v-if="game.config.funding" top color="green600" content-class="rounded-0">
          <template #activator="{ on }">
            <fai :icon="['fad', 'badge-percent']" class="green400--text ml-1 mb-1" v-on="on" />
          </template>
          <span>Creator is paying {{ game.config.funding }}%</span>
        </v-tooltip>
        <span
          class="ml-3 text-body-2 "
          :class="{
            'primary400--text font-weight-bold': game.isCursed,
            'blue400--text font-weight-bold': game.isCommunism,
            'text--secondary': !game.isCursed && !game.isCommunism,
          }" v-text="game.title"
        />
      </div>

      <div class="teams d-flex mb-n5 ml-n4">
        <div v-for="team in game.teams" :key="team.id" class="team">
          <BattleListingPlayer
            v-for="slot in team.slots"
            :key="slot"
            :game="game"
            :player="game.playersByTeam(team.id)[slot - 1]"
            :color="team.color"
            class="mr-3"
            @join="onJoin(team.id)"
          />
          <span v-if="game.teams.length !== 4" class="vs text--secondary text-caption font-weight-bold mr-1 px-1">
            <span>vs</span>
          </span>
        </div>
      </div>
    </div>

    <div class="rounds flex-grow-1 pa-1" :class="{'is-scrolling': showRoundTicker && game.round > 4}">
      <div
        class="crates"
        :style="{
          '--crateSize': `${crateSize}px`,
          '--offsetX': `-${cratesOffset}px`,
        }"
      >
        <BattleListingRound
          v-for="(crate, i) in listingRounds"
          :key="i"
          :game-case="crate" :crate-size="crateSize"
          :opened="showRoundTicker && game.round > i + 1"
          @contextmenu.native.prevent="$emit('inspect', crate)"
        />
        <v-img
          v-if="showRoundTicker"
          class="crate-indicator flex-grow-0" :src="require('@/assets/img/games/wheel/arrow.png')"
          contain width="48" aspect-ratio="1"
          :style="{'--offsetX': `${roundIndicatorOffset}px`}"
        />
      </div>
      <div class="game-status text-body-2 text--secondary">
        <span v-if="[GAME_STATUS.open, GAME_STATUS.complete].includes(game.status)">{{ rounds.length }} rounds</span>
        <span v-else-if="game.status === GAME_STATUS.locked">Starting</span>
        <span v-else-if="game.status === GAME_STATUS.rolling" class="round-status text-body-1 font-weight-bold">{{ game.round }}/{{ rounds.length }}</span>

        <div v-if="game.canShowWinners" class="text-caption font-weight-bold lh-1-2">
          <scrap :amount="game.totalWon" /> won
        </div>
      </div>
    </div>


    <div v-if="game.canShowWinners" class="winners text-right align-self-center pr-3">
      <h4 class="green300--text text-caption font-weight-bold text-uppercase mb-2">
        {{ game.winners.length > 1 ? 'Winners' : 'Winner' }}
      </h4>
      <div v-for="team in winningTeams" :key="team.id" class="d-inline-block team mb-3">
        <BattleListingPlayer
          v-for="slot in team.slots"
          :key="slot"
          :game="game"
          :player="game.playersByTeam(team.id)[slot - 1]"
          :color="team.color"
          class="mr-3"
          :size="32"
        />
      </div>
    </div>

    <div v-else class="actions align-self-center text-right pr-3">
      <v-btn
        color="grey300 text--secondary"
        class="unwidth px-3"
        tile depressed :ripple="false"
      >
        <fai v-if="joinable" :icon="['fas','eye']" />
        <span v-else class="text-caption font-weight-semibold px-2">Watch game</span>
      </v-btn>

      <v-btn
        v-if="joinable"
        class="text-caption ml-2 font-weight-semibold px-2 join-btn"
        :class="joinBtnClasses"
        tile depressed :ripple="false"
        @click.native.prevent="onJoin()"
      >
        <span
          class="lh-1 px-2" :class="{
            'primary400--text': game.isCursed,
            'blue400--text': game.isCommunism,
          }"
        >

          <span v-if="game.isCommunism">
            <fai :icon="['fad', 'users']" class="blue200--text mr-1" />
            Join group
          </span>
          <span v-else>
            <fai v-show="game.isCursed" :icon="['fad', 'skull-crossbones']" class="primary100--text mr-1" />
            Join battle
          </span>
        </span>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import CaseBattlesGame from '@/store/models/games/case-battles/CaseBattlesGame'
import { GAME_STATUS } from '@/views/games/CaseBattles/utils'

import BattleListingRound from '@/views/games/CaseBattles/components/BattleListingRound'
import BattleListingPlayer from '@/views/games/CaseBattles/components/BattleListingPlayer'

export default {
  components: {
    BattleListingRound,
    BattleListingPlayer,
  },
  props: {
    game: {
      type: [CaseBattlesGame, Object],
      required: true,
    },
    crateSize: {
      type: Number,
      default: 80,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isCreator() {
      return this.user?.steamid === this.game.creatorId
    },
    joinable() {
      if (this.isCreator) return false
      if (this.game.players[this.user?.steamid]) return false
      return this.game.joinable
    },
    winningTeams() {
      const { winningTeams } = this.game.data
      if (!winningTeams) return []

      return this.game.teams.filter(i => winningTeams.includes(i.id))
    },
    rounds() {
      const { cases } = this.game
      return this.game.config.rounds.map(id => cases[id])
    },
    showRoundTicker() {
      return this.game.status === GAME_STATUS.rolling && this.game.round
    },
    listingRounds() {
      if (this.showRoundTicker) {
        return this.rounds
      }

      return Object.values(this.rounds.reduce((acc, crate) => {
        if (!acc[crate.id]) acc[crate.id] = { ...crate, amount: 0 }
        acc[crate.id].amount += 1
        return acc
      }, {}))
    },
    GAME_STATUS() {
      return GAME_STATUS
    },
    routerTo() {
      return {
        name: 'Crate Battle',
        params: { id: this.game.id },
      }
    },
    joinBtnClasses() {
      if (this.game.isCursed) {
        return 'primary700 primary200--text'
      }

      if (this.game.isCommunism) {
        return 'blue700 blue200--text'
      }

      return 'green700 green100--text'
    },
    //
    roundIndicatorOffset() {
      if (!this.showRoundTicker) return 0
      const box = (this.crateSize + 4)
      return (this.game.round * box) - (this.crateSize / 2) - 4
    },
    cratesOffset() {
      if (!this.showRoundTicker) return 0
      if (this.game.round < 4) return 0

      const box = (this.crateSize + 4)
      return (this.game.round - 4) * box - 4
    },
  },
  methods: {
    onJoin(teamId) {
      if (!this.joinable) return
      this.$emit('join', teamId)
    },
  },
}
</script>

<style lang="scss" scoped>
.battle-listing {
  display: grid;
  cursor: pointer;
  grid-template-columns: minmax(300px, 4fr) 10fr minmax(220px, 3fr);

  &:focus::before {
    display: none;
  }

  &:hover {
    transform: translateY(-4px);
  }
}

.teams {
  .team {
    .vs {
      @include xs {
        display: none;
      }
    }

    &:last-of-type {
      .vs {
        display: none;
      }
    }
  }
}

.non-funded-price {
  position: absolute;
  text-decoration: line-through;
  transform: translate(100%, -80%);

  &.is-free {
    transform: translate(20%, -80%);
  }
}

.rounds {
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    position: absolute;
    z-index: 2;
    top: 0;
    height: 100%;
    content: '';
    pointer-events: none;
  }

  &::before {
    left: 0;
    width: 30%;
    opacity: 0;
    background: linear-gradient(to right, grey(600) 20%, transparent);
    transition: opacity 0.5s ease;
  }

  &::after {
    right: 0;
    width: 30%;
    background: linear-gradient(to left, grey(600) 5%, transparent);
  }

  &.is-scrolling {
    &::before {
      opacity: 1;
    }
  }

  .crates {
    display: flex;
    transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    transform: translateX(var(--offsetX));
    will-change: transform;

    .round-case {
      width: var(--crateSize);
      height: 100%;
      margin-right: 4px;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .crate-indicator {
    position: absolute;
    top: -8px;
    left: 0;
    will-change: transform;
    transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    transform: translateX(calc(var(--offsetX) + -50%));
    pointer-events: none;
  }

  .game-status {
    position: absolute;
    z-index: 3;
    top: 50%;
    right: 0;
    text-align: right;
    transform: translateY(-50%);
    pointer-events: none;

    span {
      position: relative;
      z-index: 1;
    }

    .round-status {
      letter-spacing: 2px !important;
    }
  }
}

.join-btn {
  min-width: 132px !important;
}
</style>
